<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  /**
   * The component used to render the button - button (default), link.
   */
  component: {
    type: String,
    default: 'button',
    validator(e) {
      return ['button', 'link'].includes(e)
    },
  },

  /**
   * The available button types - button (default), submit, reset.
   */
  type: {
    type: String,
    default: 'button',
    validator(e) {
      return ['button', 'submit', 'reset'].includes(e)
    },
  },

  /**
   * The available button style variants - primary, secondary, cancel
   */
  variant: {
    type: String,
    default: 'primary',
    validator(e) {
      return ['primary', 'secondary', 'secondary-alt', 'tertiary'].includes(e)
    },
  },

  /**
   * The available button sizes - sm, lg
   */
  size: {
    type: String,
    default: 'lg',
    validator(e) {
      return ['sm', 'lg'].includes(e)
    },
  },

  /**
   * The button text (if not slot content provided)
   */
  text: {
    type: String,
  },

  /**
   * Toggle disabled state of button
   */
  disabled: {
    type: Boolean,
    default: false,
  },

  /**
   * Optional href value
   */
  href: String,
})

const button = ref(null)

const computedElement = computed(() => {
  if (props.component == 'link') {
    return 'a'
  }

  return 'button'
})

const isLink = computed(() => {
  return props.component === 'link'
})

const variantClasses = computed(() => {
  switch (props.variant) {
    case 'primary':
      return 'bg-opal text-black hover:bg-opal-dark'
    case 'secondary':
      return 'text-white hover:text-cornflower'
    case 'secondary-alt':
      return 'text-black hover:bg-black hover:text-white hover:outline-black'
    case 'tertiary':
      return 'bg-cornflower text-black hover:bg-cornflower-dark'
    default:
      return ''
  }
})

const outlineClasses = computed(() => {
  if (['secondary', 'secondary-alt'].includes(props.variant)) {
    return 'outline outline-2 outline-[currentColor]'
  }

  return ''
})

const sizeClasses = computed(() => {
  return props.size === 'sm' ? 'min-h-10 min-w-10' : 'min-h-[60px] min-w-[230px]'
})

const paddingClasses = computed(() => {
  return props.size === 'sm' ? 'px-4 py-2' : 'p-4'
})

/**
 * Trigger a click event when the space key is pressed
 *
 * Some screen readers tell users they can activate things with the 'button'
 * role, so we need to match the functionality of native HTML buttons
 *
 * See https://github.com/alphagov/govuk_elements/pull/272#issuecomment-233028270
 */
const handleKeyDownSpace = () => {
  if (computedElement.value == 'a') {
    button.value.click()
  }

  if (computedElement.value == Link) {
    const link = button.value?.$el || button.value

    if (link && link.click) {
      link.click()
    }
  }
}
</script>

<template>
  <component
    :is="computedElement"
    ref="button"
    :type="type"
    class="relative inline-block text-center text-sm font-bold -outline-offset-2 focus:bg-yellow focus:text-black focus:shadow-[0_3px_#333] focus:outline-none disabled:bg-gray-300 disabled:font-normal disabled:text-black sm:text-base rounded-md"
    :class="[variantClasses, outlineClasses, sizeClasses, paddingClasses]"
    :disabled="disabled ? 'disabled' : null"
    :aria-disabled="disabled ? 'true' : null"
    :href="isLink ? (href ? href : '#') : null"
    :role="isLink ? 'button' : null"
    @keydown.space="handleKeyDownSpace"
  >
    <slot>{{ text }}</slot>
  </component>
</template>
